import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import LoadingScreen from 'src/components/LoadingScreen';
import MainLayout from 'src/layouts/MainLayout';
import AuthGuard from 'src/components/AuthGuard';
import { isChunkTestEnabled } from 'src/config';

// 💡 チャンク読み込み失敗を意図的に発生させる関数
const simulateChunkLoadFailure = () =>
  new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Simulated chunk load failure')), 100)
  );

const LazyFailingComponent = lazy(simulateChunkLoadFailure);

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    path: '/app',
    layout: MainLayout,
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/app/home',
        component: lazy(() => import('src/views/HomeView'))
      },
      {
        exact: true,
        path: '/app/auth_groups',
        component: lazy(() => import('src/views/auth_group/AuthGroupListView'))
      },
      {
        exact: true,
        path: '/app/auth_groups/:id',
        component: lazy(() =>
          import('src/views/auth_group/AuthGroupDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/admission_people',
        component: lazy(() =>
          import('src/views/admission_person/AdmissionPersonListView')
        )
      },
      {
        exact: true,
        path: '/app/admission_people/create',
        component: lazy(() =>
          import('src/views/admission_person/AdmissionPersonCreateView')
        )
      },
      {
        exact: true,
        path: '/app/admission_people/bulk_create',
        component: lazy(() =>
          import('src/views/admission_person/AdmissionPersonBulkCreateView')
        )
      },
      {
        exact: true,
        path: '/app/admission_people/:id',
        component: lazy(() =>
          import('src/views/admission_person/AdmissionPersonDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/admission_people/:id/edit',
        component: lazy(() =>
          import('src/views/admission_person/AdmissionPersonEditView')
        )
      },
      {
        exact: true,
        path: '/app/carry_requests',
        component: lazy(() =>
          import('src/views/carry_request/CarryRequestListView')
        )
      },
      {
        exact: true,
        path: '/app/carry_requests/create',
        component: lazy(() =>
          import('src/views/carry_request/CarryRequestCreateView')
        )
      },
      {
        exact: true,
        path: '/app/carry_requests/:id',
        component: lazy(() =>
          import('src/views/carry_request/CarryRequestDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/carry_requests/:id/edit',
        component: lazy(() =>
          import('src/views/carry_request/CarryRequestEditView')
        )
      },
      {
        exact: true,
        path: '/app/mdf_admission_requests',
        component: lazy(() =>
          import('src/views/mdf_admission_request/MdfAdmissionRequestListView')
        )
      },
      {
        exact: true,
        path: '/app/mdf_admission_requests/create',
        component: lazy(() =>
          import(
            'src/views/mdf_admission_request/MdfAdmissionRequestCreateView'
          )
        )
      },
      {
        exact: true,
        path: '/app/mdf_admission_requests/:id',
        component: lazy(() =>
          import(
            'src/views/mdf_admission_request/MdfAdmissionRequestDetailsView'
          )
        )
      },
      {
        exact: true,
        path: '/app/mdf_admission_requests/:id/edit',
        component: lazy(() =>
          import('src/views/mdf_admission_request/MdfAdmissionRequestEditView')
        )
      },
      {
        exact: true,
        path: '/app/history/admission_histories',
        component: lazy(() =>
          import('src/views/history/AdmissionHistoryListView')
        )
      },
      {
        exact: true,
        path: '/app/exported_csvs/:id',
        component: lazy(() =>
          import('src/views/exported_csv/ExportedCsvDownloadView')
        )
      },
      {
        exact: true,
        path: '/app/test-chunk-error',
        component: isChunkTestEnabled
          ? LazyFailingComponent
          : () => <Redirect to="/error/404" />
      },
      {
        path: '*',
        component: () => <Redirect to="/error/404" />
      }
    ]
  },
  {
    path: '/error',
    routes: [
      {
        exact: true,
        path: '/error/401',
        component: lazy(() => import('src/views/error/401'))
      },
      {
        exact: true,
        path: '/error/404',
        component: lazy(() => import('src/views/error/404'))
      },
      {
        exact: true,
        path: '/error/500',
        component: lazy(() => import('src/views/error/500'))
      },
      {
        path: '*',
        component: () => <Redirect to="/error/404" />
      }
    ]
  },
  {
    exact: true,
    path: '/',
    guard: AuthGuard,
    component: () => <Redirect to="/app/home" />
  },
  {
    path: '*',
    component: () => <Redirect to="/error/404" />
  }
];
export default routes;
